import {useState, useEffect, useRef, useCallback} from 'react';
import {
  LiveAnswersClient,
  LiveAnswersMessage,
  LiveAnswersMessageData,
  StripeAssistantLiveAnswersSource,
} from '@stripe-internal/conversations-client';
import {view} from '@sail/ui';
import {useAnalytics} from '../../AnalyticsProvider';
import {
  InlineLinkClickHandler,
  InlineLinkProps,
} from '../../StripeAssistant/LiveAnswersV2/InlineLinkClickHandler';
import {ChatBubbleDotCom} from './ChatBubbleDotCom';
import {TextBody} from './TextBody';
import {useWordChunkStreamer} from '../DotCom/useWordChunkStreamer';
import {TypingRowDotCom} from './TypingRowDotCom';
import {useDotComFeedbackButtons} from '../../FeatureFlagsProvider';
import {DotComSourcesDropdown} from '../DotCom/Sources/DotComSourcesDropdown';
import {FeedbackButtons} from '../DotCom/FeedbackButtons';

type Props = {
  liveAnswersMessage: LiveAnswersMessage;
  liveAnswersClient: LiveAnswersClient;
  onDoneStreaming: (
    content: string,
    answerType?: 'unanswerable' | 'answerable',
  ) => void;
  onError: (error?: string) => void;
};

const STREAMING_POLL_INTERVAL_MS = 1000;

export const inlineLinkClickedActionEvent =
  'universal_help_live_answers_frontend_inline_link_clicked';

function DotComStreamingOutput({
  textStream,
  messageData,
  sources = [],
  doneStreaming = false,
  disableAnimation = false,
}: {
  textStream: string;
  messageData: LiveAnswersMessageData;
  sources: StripeAssistantLiveAnswersSource[];
  doneStreaming?: boolean;
  disableAnimation?: boolean;
}) {
  const analytics = useAnalytics();
  const feedbackButtons = useDotComFeedbackButtons();

  const completedSuccessfully =
    messageData.is_complete &&
    !messageData.was_halted &&
    !messageData.not_answerable;
  const showSources =
    completedSuccessfully && doneStreaming && sources.length > 0;

  const getBubbleAnimation = (index: number) => {
    if (disableAnimation) {
      return 'disabled';
    }

    return index === 0 ? 'skip-scale' : 'all';
  };

  const rows = textStream
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line, i) => (
      <ChatBubbleDotCom
        // eslint-disable-next-line react/no-array-index-key
        key={`live-answers-response-${i}`}
        animation={getBubbleAnimation(i)}
        background="neutral100"
      >
        <InlineLinkClickHandler
          onClick={({href, innerText, parentInnerText}: InlineLinkProps) => {
            analytics.action(inlineLinkClickedActionEvent, {
              live_answers_source_url: href,
              live_answers_source_title: innerText,
              live_answers_source_context: parentInnerText,
            });
          }}
        >
          <TextBody body={line} background="neutral100" />
        </InlineLinkClickHandler>
      </ChatBubbleDotCom>
    ));

  return (
    <view.div
      css={{
        stack: 'y',
        gap: 'small',
        width: '100%',
      }}
    >
      {rows}
      {showSources ? (
        <view.div
          css={{
            stack: 'x',
            gap: 'small',
            width: '100%',
          }}
        >
          <view.div
            css={{
              width: 'calc(100% - 60px)',
            }}
          >
            <DotComSourcesDropdown sources={sources} />
          </view.div>
          {feedbackButtons ? (
            <FeedbackButtons streamingConversationId={messageData.streamId} />
          ) : null}
        </view.div>
      ) : null}
    </view.div>
  );
}

export function LiveAnswersRowDotCom({
  liveAnswersMessage,
  liveAnswersClient,
  onDoneStreaming,
  onError,
}: Props) {
  const alreadyCompleted = !!liveAnswersMessage.data?.is_complete;
  const [showSources, setShowSources] = useState<boolean>(
    alreadyCompleted && liveAnswersMessage.sources.length > 0,
  );
  const [completed, setCompleted] = useState<boolean>(alreadyCompleted);

  const [doneStreaming, setDoneStreaming] = useState<boolean>(alreadyCompleted);
  const [errored, setErrored] = useState<boolean>(false);
  const [cancelled, setCancelled] = useState<boolean>(
    !!liveAnswersMessage.data?.was_halted,
  );
  const messageData = useRef<LiveAnswersMessageData | undefined>(
    liveAnswersMessage.data,
  );

  const polling = useRef<boolean>(false);
  const abortController = useRef<AbortController | undefined>(undefined);
  const onDoneStreamingRef = useRef<boolean>(false);

  const onFinishedStreaming = useCallback(() => {
    if (alreadyCompleted || onDoneStreamingRef.current) {
      return;
    }

    if (abortController.current?.signal.aborted) {
      return;
    }

    if (messageData.current?.is_complete) {
      onDoneStreamingRef.current = true;
      setDoneStreaming(true);
      onDoneStreaming(
        messageData.current?.content || '',
        messageData.current?.not_answerable ? 'unanswerable' : 'answerable',
      );
    }
  }, [alreadyCompleted, onDoneStreaming]);

  const {textStream, setText} = useWordChunkStreamer({
    initialText: liveAnswersMessage.data?.content || '',
    disabled: doneStreaming,
    onFinished: onFinishedStreaming,
  });

  useEffect(() => {
    if (completed || alreadyCompleted) {
      return;
    }

    let timeout: ReturnType<typeof setTimeout> | undefined;
    async function poll() {
      if (
        polling.current ||
        messageData.current?.is_complete ||
        messageData.current?.was_halted ||
        messageData.current?.not_answerable
      ) {
        return;
      }

      polling.current = true;

      try {
        abortController.current = new AbortController();
        const abortSignal = abortController.current.signal;

        const resp = await liveAnswersClient.fetchMessage(liveAnswersMessage, {
          abortSignal,
        });

        polling.current = false;

        if (abortSignal.aborted) {
          return;
        }

        if (resp.code === 200 && resp.data?.type === 'success') {
          const {data: respData} = resp;
          const {message} = respData;
          const {data: msgData} = message;

          messageData.current = msgData;

          setText(msgData.content);

          if (
            msgData.is_complete ||
            msgData.was_halted ||
            msgData.not_answerable
          ) {
            if (msgData.was_halted) {
              setCancelled(true);
              setShowSources(false);
              onDoneStreaming('');
            } else {
              setShowSources(!msgData.not_answerable);
            }
          } else {
            timeout = setTimeout(poll, STREAMING_POLL_INTERVAL_MS);
          }
        } else {
          setCompleted(true);
          setErrored(true);
          let error =
            'An error occurred while fetching the live answers message.';
          if (resp.data?.type === 'error' && resp.data.error) {
            error = resp.data.error;
          } else if (resp.error) {
            error = resp.error;
          }
          onError(error);
        }
      } catch (e) {
        polling.current = false;
        setErrored(true);
        onError(e instanceof Error ? e.message : String(e));
      }
    }

    poll();

    return () => {
      abortController.current?.abort();
      polling.current = false;
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [
    alreadyCompleted,
    completed,
    liveAnswersClient,
    liveAnswersMessage,
    onDoneStreaming,
    onError,
    setText,
  ]);

  const trimmedContent = textStream.trim();

  if (completed && trimmedContent === '') {
    return null;
  }

  if (errored) {
    return null;
  }

  if (!messageData.current || trimmedContent === '') {
    return <TypingRowDotCom animateIn={false} />;
  }

  // want to use the initial message data to determine if we should disable animation
  const disableAnimation = alreadyCompleted || cancelled;

  return (
    <DotComStreamingOutput
      textStream={trimmedContent}
      messageData={messageData.current}
      sources={showSources ? liveAnswersMessage.sources : []}
      doneStreaming={doneStreaming}
      disableAnimation={disableAnimation}
    />
  );
}
